/**
 * BILLING-S1B — PlanCard pra tela /planos read-only.
 *
 * Props:
 *   plan: doc do /api/plans (com slug, name, price_brl_monthly, etc)
 *   cycle: "monthly" | "annual"
 *   isFeatured: bool (Pro tem MAIS POPULAR + glow mais intenso)
 *   onSelect(slug, cycle): handler do CTA
 *
 * Identidade visual mantida: glass-card + border-left por tier.
 * Sem dep nova — Tailwind classes + style inline.
 */
import { motion } from "framer-motion";
import { Check, X, Sparkles } from "lucide-react";
import {
  humanizePlanFeatures,
  formatBrlPrice,
  PLAN_SUBTITLE,
  PLAN_TIER_COLOR,
  ctaLabelFor,
} from "@/utils/planFeatures";

export default function PlanCard({ plan, cycle = "monthly", isFeatured = false, onSelect }) {
  if (!plan) return null;
  const slug = plan.slug;
  const tierColor = PLAN_TIER_COLOR[slug] || "#06b6d4";
  const subtitle = PLAN_SUBTITLE[slug] || (plan.features?.[0] ?? "");

  const monthlyPrice = plan.price_brl_monthly;
  const annualMonthlyEquiv = plan.price_brl_annual;

  const showAnnual = cycle === "annual";
  const displayedPrice = showAnnual ? annualMonthlyEquiv : monthlyPrice;
  const strikePrice = showAnnual ? monthlyPrice : null;

  const features = humanizePlanFeatures(plan);

  const handleClick = () => {
    onSelect?.(slug, cycle);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: isFeatured ? 1.02 : 1,
      }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className={`glass-card rounded-2xl p-6 relative flex flex-col h-full ${
        isFeatured ? "shadow-[0_0_40px_-10px_rgba(59,130,246,0.45)]" : ""
      }`}
      style={{
        borderLeft: `3px solid ${tierColor}`,
      }}
      data-testid={`plan-card-${slug}`}
      data-featured={isFeatured ? "true" : "false"}
    >
      {/* Chip MAIS POPULAR */}
      {isFeatured && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"
          style={{
            backgroundColor: `${tierColor}20`,
            color: tierColor,
            border: `1px solid ${tierColor}40`,
          }}
          data-testid={`plan-card-featured-badge-${slug}`}
        >
          <Sparkles className="w-3 h-3" />
          Mais popular
        </div>
      )}

      {/* Nome + subtitle */}
      <div className="mb-5">
        <h3
          className="font-display text-2xl font-bold text-white"
          data-testid={`plan-name-${slug}`}
        >
          {plan.name}
        </h3>
        {subtitle && (
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        )}
      </div>

      {/* Preço */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span
            className="font-display text-4xl font-bold text-white tabular-nums"
            data-testid={`plan-price-${slug}`}
          >
            {formatBrlPrice(displayedPrice)}
          </span>
          <span className="text-sm text-slate-500">
            {showAnnual ? "/mês equiv." : "/mês"}
          </span>
        </div>
        {strikePrice != null && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-slate-600 line-through">
              {formatBrlPrice(strikePrice)} mensal
            </span>
            <span
              className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: "#f59e0b20",
                color: "#f59e0b",
              }}
            >
              −20%
            </span>
          </div>
        )}
      </div>

      {/* Features list */}
      <ul
        className="space-y-2 mb-6 flex-1"
        data-testid={`plan-features-${slug}`}
      >
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-sm"
          >
            {f.included ? (
              <Check
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                style={{ color: tierColor }}
                aria-hidden="true"
              />
            ) : (
              <X
                className="w-4 h-4 mt-0.5 text-slate-600 flex-shrink-0"
                aria-hidden="true"
              />
            )}
            <span className={f.included ? "text-slate-300" : "text-slate-600"}>
              {f.text}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <motion.button
        type="button"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
        className="w-full py-3 px-4 rounded-xl font-medium text-sm transition-all"
        style={{
          backgroundColor: `${tierColor}18`,
          color: tierColor,
          border: `1px solid ${tierColor}40`,
          boxShadow: isFeatured ? `0 0 20px -8px ${tierColor}66` : "none",
        }}
        data-testid={`plan-cta-${slug}`}
        aria-label={ctaLabelFor(plan)}
      >
        {ctaLabelFor(plan)}
      </motion.button>
    </motion.div>
  );
}
